import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Briefcase, Mail, Lock, User, Phone, Building2, ArrowRight, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useRecaptcha } from "../hooks/useRecaptcha";
import SEO from "../components/SEO";
import { trackSignUp } from "../utils/analytics";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { executeRecaptcha } = useRecaptcha();
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/[\s\-\.]/g, '');
    return /^(\+33|0033|0)[1-9][0-9]{8}$/.test(cleaned);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error("Veuillez accepter les Conditions Générales d'Utilisation et la Politique de Confidentialité");
      return;
    }
    
    if (!formData.phoneNumber || !validatePhone(formData.phoneNumber)) {
      toast.error("Veuillez saisir un numéro de téléphone valide (ex: 06 12 34 56 78)");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);

    try {
      // Execute reCAPTCHA v3
      const captchaToken = await executeRecaptcha('register');
      
      const fullName = `${formData.firstName} ${formData.lastName}`.trim() || formData.email.split('@')[0];
      await register(
        formData.email, 
        formData.password, 
        fullName,
        formData.firstName,
        formData.lastName,
        formData.phoneNumber,
        formData.companyName,
        captchaToken
      );
      trackSignUp("email");
      toast.success("Compte créé avec succès !");
      navigate("/profile");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 items-center justify-center p-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center text-white max-w-lg"
        >
          <div className="w-24 h-24 rounded-3xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-8">
            <Briefcase className="w-12 h-12 text-white" />
          </div>
          <h2 className="font-display text-3xl font-bold mb-4">
            Rejoignez Mon DCE Express
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Créez votre compte gratuit et commencez à recevoir des recommandations personnalisées de marchés publics.
          </p>
          <div className="grid grid-cols-2 gap-6 text-left">
            <div className="bg-white/5 rounded-xl p-4 backdrop-blur-sm">
              <div className="text-2xl font-bold">3</div>
              <div className="text-slate-400 text-sm">Recommandations / semaine</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 backdrop-blur-sm">
              <div className="text-2xl font-bold">100%</div>
              <div className="text-slate-400 text-sm">Gratuit pour commencer</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <SEO 
            title="Créer un compte"
            description="Créez votre compte DCE Express gratuit pour recevoir des recommandations personnalisées d'appels d'offres."
            path="/register"
            noindex={true}
          />
          <Link to="/" className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-xl text-slate-900">Mon DCE Express</span>
          </Link>

          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-violet-600 transition-colors mb-4"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0 py-3">
              <CardTitle className="font-display text-2xl font-bold text-slate-900">
                Créer un compte
              </CardTitle>
              <CardDescription className="text-slate-600 text-sm">
                Commencez gratuitement et trouvez vos marchés publics
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="companyName" className="text-slate-700">Nom de l'entreprise</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Ma Société SARL"
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      className="pl-10 h-11 border-slate-200 focus:ring-violet-500"
                      data-testid="register-company-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName" className="text-slate-700">Prénom</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="firstName"
                        type="text"
                        placeholder="Jean"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="pl-10 h-11 border-slate-200 focus:ring-violet-500"
                        data-testid="register-firstname-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName" className="text-slate-700">Nom</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="lastName"
                        type="text"
                        placeholder="Dupont"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="pl-10 h-11 border-slate-200 focus:ring-violet-500"
                        data-testid="register-lastname-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="phoneNumber" className="text-slate-700">Téléphone *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="phoneNumber"
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                      className="pl-10 h-11 border-slate-200 focus:ring-violet-500"
                      data-testid="register-phone-input"
                      required
                    />
                  </div>
                  <p className="text-xs text-slate-500">Pour vous contacter concernant les marchés</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-slate-700">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 h-11 border-slate-200 focus:ring-violet-500"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-slate-700">Mot de passe *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-10 h-11 border-slate-200 focus:ring-violet-500"
                        required
                        data-testid="register-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword" className="text-slate-700">Confirmer *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        className="pl-10 h-11 border-slate-200 focus:ring-violet-500"
                        required
                        data-testid="register-confirm-password-input"
                      />
                    </div>
                  </div>
                </div>

                {/* RGPD Consent Checkbox */}
                <div className="flex items-start gap-3 mt-4 p-3 bg-slate-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 text-violet-600 border-slate-300 rounded focus:ring-violet-500"
                    data-testid="register-accept-terms-checkbox"
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-slate-600 leading-relaxed">
                    J'accepte les{" "}
                    <Link to="/legal" className="text-violet-600 hover:underline font-medium" target="_blank">
                      Conditions Générales d'Utilisation
                    </Link>{" "}
                    et la{" "}
                    <Link to="/legal#rgpd" className="text-violet-600 hover:underline font-medium" target="_blank">
                      Politique de Confidentialité
                    </Link>
                    . Je consens au traitement de mes données personnelles conformément au RGPD.
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !acceptedTerms}
                  className="w-full h-11 bg-violet-700 hover:bg-violet-800 text-white rounded-full text-base font-medium mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Créer mon compte
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <p className="text-slate-600 text-sm">
                  Déjà un compte ?{" "}
                  <Link 
                    to="/login" 
                    className="text-violet-700 hover:text-violet-800 font-medium"
                    data-testid="register-login-link"
                  >
                    Se connecter
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
